import { IChecklistTemplateController } from "Interfaces/Controllers/IChecklistTemplateController";
import { IEnvConfigUtils } from "Interfaces/Utils/IEnvConfigUtils";
import { inject, injectable } from "inversify";
import { ChecklistItemTemplate } from "Models/Checklists/ChecklistItemTemplate";
import { ChecklistSuggestion } from "Models/Checklists/ChecklistSuggestion";
import { ChecklistTemplate } from "Models/Checklists/ChecklistTemplate";
import BadRequestException from "Models/Exceptions/BadRequestException";
import { SuggestionFilter } from "Models/Filters/SuggestionFilter";
import { TaskTemplateStep } from "Models/Tasks/TaskTemplateStep";
import Types from "ServiceProvider/Types";
import i18n from "Translations/i18n";
import request from "../BaseRequest";
import { BaseTemplateController } from "./BaseTemplateController";

@injectable()
export class ChecklistTemplateController extends BaseTemplateController<ChecklistTemplate> implements IChecklistTemplateController {
    constructor(@inject(Types.IEnvConfigUtils) envConfigUtils: IEnvConfigUtils) {
        super(envConfigUtils, "checklisttemplates");
    }

    public async getSuggestionsByFilterAsync(filter: SuggestionFilter): Promise<ChecklistSuggestion[]> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/suggestions`)
            .query(filter)
            .set(this.headers);

        return response.body as ChecklistSuggestion[];
    }

    public async getOneSuggestionAsync(checklistTemplateId: number): Promise<ChecklistSuggestion> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/suggestions/${checklistTemplateId}`)
            .set(this.headers);

        return response.body as ChecklistSuggestion;
    }

    public async insertOneAsync(checklistTemplate: ChecklistTemplate): Promise<ChecklistTemplate> {
        this.init();

        const result = await request
            .post(this.endpoint)
            .send(checklistTemplate)
            .set(this.headers)
            .then((response: any) => {
                return response.body as ChecklistTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async updateOneAsync(checklistTemplate: ChecklistTemplate): Promise<ChecklistTemplate> {
        this.init();

        const result = await request
            .put(`${this.endpoint}/${checklistTemplate.id}`)
            .send(checklistTemplate)
            .set(this.headers)
            .then((response: any) => {
                return response.body as ChecklistTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async duplicateOneAsync(checklistTemplate: ChecklistTemplate): Promise<ChecklistTemplate> {
        this.init();

        const result = await request
            .post(`${this.endpoint}/${checklistTemplate.id}/duplicate`)
            .send({ duplicatePrefix: i18n.t("common.copyOf") })
            .set(this.headers)
            .then((response: any) => {
                return response.body as ChecklistTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async deleteByIdAsync(checklistTemplateId: number): Promise<void> {
        this.init();

        await request
            .delete(`${this.endpoint}/${checklistTemplateId}`)
            .set(this.headers);
    }

    private handleSaveError(error: any): Error {
        if (error.response.badRequest) {
            return new BadRequestException(error.response.body.validationErrors);
        }
        else {
            return error;
        }
    }

    public async uploadMediaAsync(checklistTemplate: ChecklistTemplate, video?: File, videoThumbnail?: File, picture?: File, description?: File): Promise<ChecklistTemplate> {
        this.init();

        const formData = new FormData();
        video && formData.append("video", video, video.name);
        videoThumbnail && formData.append("videoThumbnail", videoThumbnail, videoThumbnail.name);
        picture && formData.append("picture", picture, picture.name);
        description && formData.append("description", description, description.name);

        const response = await request
            .post(`${this.endpoint}/${checklistTemplate.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as ChecklistTemplate;
    }

    public async uploadItemMediaAsync(checklistTemplate: ChecklistTemplate, item: ChecklistItemTemplate): Promise<ChecklistItemTemplate> {
        this.init();

        const formData = new FormData();
        item.videoFile && formData.append("video", item.videoFile);
        item.videoThumbnailFile && formData.append("videoThumbnail", item.videoThumbnailFile);
        item.pictureFile && formData.append("picture", item.pictureFile);
        item.descriptionFile && formData.append("description", item.descriptionFile, item.descriptionFileUrl);

        const response = await request
            .post(`${this.endpoint}/${checklistTemplate.id}/items/${item.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as ChecklistItemTemplate;
    }

    public async uploadStepMediaAsync(checklistTemplate: ChecklistTemplate, item: ChecklistItemTemplate, step: TaskTemplateStep): Promise<TaskTemplateStep> {
        this.init();

        const formData = new FormData();
        step.videoFile && formData.append("video", step.videoFile);
        step.videoThumbnailFile && formData.append("videoThumbnail", step.videoThumbnailFile);
        step.pictureFile && formData.append("picture", step.pictureFile);

        const response = await request
            .post(`${this.endpoint}/${checklistTemplate.id}/items/${item.id}/steps/${step.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as TaskTemplateStep;
    }

}


// WEBPACK FOOTER //
// ./src/Apis/Controllers/ChecklistTemplateController.ts